import * as React from 'react';
import { Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  formatSupplyDate,
  resolveSupplyStatusLabel,
  resolveSupplyStatusTone,
  type SupplyAppSelection,
  type SupplyDomainSelection,
} from '../../model';

type SelectionSectionProps<TItem> = {
  items: TItem[];
  title: string;
  manageLabel: string;
  description: string;
  emptyMessage: string;
  getId: (item: TItem) => string;
  getName: (item: TItem) => string;
  columns: ReturnType<typeof buildDashboardDataTableColumns<TItem>>;
  onManage: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
};

function SelectionSection<TItem>({
  items,
  title,
  manageLabel,
  description,
  emptyMessage,
  getId,
  getName,
  columns,
  onManage,
  onRemove,
  onClear,
}: SelectionSectionProps<TItem>) {
  const tableColumns = React.useMemo(
    () => [
      ...columns,
      ...buildDashboardDataTableColumns<TItem>([
        {
          kind: DashboardDataTableColumnKind.Actions,
          id: 'actions',
          cell: (item) => (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={dashboardT('supply.removeNamed', { name: getName(item) })}
              onClick={() => {
                onRemove(getId(item));
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ),
        },
      ]),
    ],
    [columns, getId, getName, onRemove],
  );
  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(items);
  return (
    <div className="grid min-w-0 gap-3">
      <div className="grid gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-foreground text-[26px] leading-none font-bold tracking-normal">
            {title}
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={onManage}>
              {manageLabel}
            </Button>
            <Button type="button" variant="outline" disabled={!items.length} onClick={onClear}>
              <Trash2 className="h-4 w-4" />
              {dashboardT('supply.clear')}
            </Button>
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs">{description}</p>
        </div>
      </div>
      <DashboardDataTable
        tableLabel={title}
        columns={tableColumns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={getId}
        emptyMessage={emptyMessage}
        minWidthClassName="min-w-[560px]"
        surfaceClassName="border-input shadow-none!"
      />
    </div>
  );
}

export function SelectedAppsSection(
  props: Omit<
    SelectionSectionProps<SupplyAppSelection>,
    'title' | 'manageLabel' | 'description' | 'emptyMessage' | 'getId' | 'getName' | 'columns'
  >,
) {
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<SupplyAppSelection>([
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'name',
          header: dashboardT('supply.name'),
          headerClassName: 'pl-4',
          className: 'pl-4',
          value: (item) => item.name,
        },
        {
          kind: DashboardDataTableColumnKind.Status,
          id: 'status',
          header: dashboardT('supply.status'),
          label: (item) => resolveSupplyStatusLabel(item.status),
          tone: (item) => resolveSupplyStatusTone(item.status),
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'appStoreId',
          header: dashboardT('supply.appStoreId'),
          value: (item) => item.appStoreId,
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'bundleId',
          header: dashboardT('supply.bundleId'),
          value: (item) => item.bundleId,
        },
        {
          kind: DashboardDataTableColumnKind.Date,
          id: 'created',
          header: dashboardT('supply.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (item) => formatSupplyDate(item.createdOn),
        },
      ]),
    [],
  );
  return (
    <SelectionSection
      {...props}
      title={dashboardT('supply.apps')}
      manageLabel={dashboardT('supply.selectApps')}
      description={dashboardT('supply.selectedAppsDescription')}
      emptyMessage={dashboardT('supply.noAppsSelected')}
      getId={(item) => item.id}
      getName={(item) => item.name}
      columns={columns}
    />
  );
}

export function SelectedDomainsSection(
  props: Omit<
    SelectionSectionProps<SupplyDomainSelection>,
    'title' | 'manageLabel' | 'description' | 'emptyMessage' | 'getId' | 'getName' | 'columns'
  >,
) {
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<SupplyDomainSelection>([
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'url',
          header: dashboardT('supply.url'),
          headerClassName: 'pl-4',
          className: 'pl-4',
          value: (item) => item.url,
        },
        {
          kind: DashboardDataTableColumnKind.Status,
          id: 'status',
          header: dashboardT('supply.status'),
          label: (item) => resolveSupplyStatusLabel(item.status),
          tone: (item) => resolveSupplyStatusTone(item.status),
        },
        {
          kind: DashboardDataTableColumnKind.Date,
          id: 'created',
          header: dashboardT('supply.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (item) => formatSupplyDate(item.createdOn),
        },
      ]),
    [],
  );
  return (
    <SelectionSection
      {...props}
      title={dashboardT('supply.domains')}
      manageLabel={dashboardT('supply.selectDomains')}
      description={dashboardT('supply.selectedDomainsDescription')}
      emptyMessage={dashboardT('supply.noDomainsSelected')}
      getId={(item) => item.id}
      getName={(item) => item.url}
      columns={columns}
    />
  );
}
